import React, { useEffect, useState } from 'react';
import PageHeader from '../../../components/layout/LayoutWrapper/PageHeader';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getInvoiceDetails } from '../../../utils/urls';
import CustomChip from '../../../components/common/CustomChip';
import CustomPaperBox from '../../../components/common/CustomPaperBox';
import ServerPaginationDataGridWithReactQuery from '../../../components/common/CustomDataGrid/ServerPaginationDataGridWithReactQuery';
import Grid from '../../../components/layout/grid/Grid';

const PaymentDetails = () => {
  const { state } = useLocation();
  const invoice_id = state?.id || '';
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    if (!invoice_id) return;

    const fetchInvoiceData = async () => {
      try {
        const url = getInvoiceDetails(invoice_id);
        const { data } = await axios.get(url);
        setInvoiceData(data?.data);
      } catch (error) {
        toast.error('Failed to fetch invoice details');
      }
    };

    fetchInvoiceData();
  }, [invoice_id]);


  const getStatusVariant = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partially Paid':
        return 'warning';
      case 'Not-Paid':
      case 'Pending':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;
  const pendingAmount = invoiceData?.generated_amount - invoiceData?.paid_amount;
  const totalAfterTax = (invoiceData?.generated_amount || 0) + (invoiceData?.tax || 0);

  const displayFields = [
    { label: 'Advisor Name', value: invoiceData?.advisor.name },
    { label: 'Payment Done On', value: invoiceData?.payment_done_on },
    { label: 'Invoice Generated On', value: invoiceData?.invoice_date },
    { label: 'Total Amount', value: formatCurrency(invoiceData?.generated_amount) },
    { label: 'Received Amount', value: formatCurrency(invoiceData?.paid_amount) },
    { label: 'Pending Amount', value: formatCurrency(pendingAmount) },
  ];

  const columns = [
    { field: 'lead_name', headerName: 'Lead Name', width: 180 },
    { field: 'product_name', headerName: 'Product Name', width: 200 },
    { field: 'product_tax', headerName: 'Product Tax', width: 150 },
    { field: 'amount', headerName: 'Commission', width: 180 },
    {
      field: 'payment_status',
      headerName: 'Payment Status',
      width: 180,
      renderCell: (params) => {
        const status = params.payment_status;
    
        const getStatusVariant = (status) => {
          switch (status) {
            case 'Paid':
              return 'success';
            case 'Partially Paid':
              return 'warning';
            case 'Pending':
            case 'Not-Paid':
              return 'error';
            default:
              return 'default';
          }
        };
    
        return (
          <CustomChip
            label={status}
            variant={getStatusVariant(status)}
            size="sm"
          />
        );
      }
    },    
    { field: 'pending_amount', headerName: 'Pending Amount', width: 180 },
  ];


  return (
    <div className="p-4">
      <PageHeader title="Invoice Details" showBack={true} size="lg" />

      <CustomPaperBox className="mt-4 p-4" elevation="sm" variant="default">
        <div className="flex items-center gap-3">
          <h3 className="font-bold text-lg">#{invoice_id}</h3>
          {invoiceData?.invoice_payment_status && (
            <CustomChip
              label={invoiceData.invoice_payment_status}
              variant={getStatusVariant(invoiceData.invoice_payment_status)}
              size="md"
            />
          )}
        </div>

        <Grid gap={20} className="my-5">
          {displayFields.map(({ label, value }) => (
            <Grid key={label} item xs={12} md={4}>
              <span className="text-gray-500 text-sm">{label}:</span>
              <span className="pl-2 font-medium">{value || 'N/A'}</span>
            </Grid>
          ))}
        </Grid>

        <ServerPaginationDataGridWithReactQuery
          url={getInvoiceDetails(invoice_id)}
          requestParameter={{ invoice_id }}
          limit={10}
          columns={columns}
          isServerPagination={true}
          keyId="lead_id"
          dataMapper={(data) => {
            return data?.product.map(item => {
              const paid = item.paid_amount || 0;
              const total = item.amount || 0;
          
              let paymentStatus = 'Pending';
              if (paid === 0) {
                paymentStatus = 'Pending';
              } else if (paid >= total) {
                paymentStatus = 'Paid';
              } else if (paid > 0 && paid < total) {
                paymentStatus = 'Partially Paid';
              }
          
              return {
                lead_name: item.lead?.name,
                product_name: item.product?.product_name,
                product_tax: item.product_tax || 0,
                amount: item.amount,
                payment_status: paymentStatus,
                pending_amount: formatCurrency(total - paid),
              };
            }) || [];
          }}          
        />

        <div className="flex flex-col gap-2 px-2 text-right">
          <div className="flex justify-end">
            <span className="text-gray-500 text-sm w-24 text-left">Subtotal:</span>
            <span className="pl-2 font-medium w-32 text-right">{formatCurrency(invoiceData?.generated_amount)}</span>
          </div>
          <div className="flex justify-end">
            <span className="text-gray-500 text-sm w-24 text-left">Tax:</span>
            <span className="pl-2 font-medium w-32 text-right">{formatCurrency(invoiceData?.tax || 0)}</span>
          </div>
          <div className="flex justify-end">
            <span className="text-gray-500 text-sm w-24 text-left">Total:</span>
            <span className="pl-2 font-medium w-32 text-right">
              {formatCurrency(totalAfterTax)}
            </span>
          </div>
        </div>
      </CustomPaperBox>
    </div>
  );
};

export default PaymentDetails;
